<script lang="ts" setup>
import { Delta, Quill, QuillEditor } from "@vueup/vue-quill";
import "@vueup/vue-quill/dist/vue-quill.snow.css";
import {
  getDownloadURL,
  ref as storageRef,
  uploadBytesResumable,
  UploadTask,
} from "firebase/storage";

import { v4 as uuidv4 } from "uuid";
import { useFirebaseStorage } from "vuefire";
import { ImageUploadBlot } from "~/blots/ImageUploadBlot";
import { SectionHeaderBlot } from "~/blots/SectionHeader";
import { TableOfContentsBlot } from "~/blots/TableOfContents";

// from the index, which exports a lot of useful modules

// @ts-ignore
import BlotFormatter from "quill-blot-formatter/dist/BlotFormatter";

const props = defineProps({
  modelValue: {
    type: Object as PropType<any>,
  },
  readOnly: {
    type: Boolean,
    default: false,
  },
  showFormattingOptions: {
    type: Boolean,
    default: true,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  minHeight: {
    type: Number,
    default: 200,
    required: false,
  },
  showAdvancedToolbar: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  "update:modelValue",
  "update:plaintext",
  "onSave",
  "onClose",
]);

const editor = ref<Quill | undefined>();

const quillToolbarId = ref(`quill-toolbar-${useGenerateUID()}`);
const lastCursorPosition = ref<number>(0);

onMounted(() => {
  // const quillEditor = editor.value.quill;
  // quillEditor.container.addEventListener("keyup", onKeyUp);
  // initializeQuillEvents();
  Quill.register(SectionHeaderBlot);
  Quill.register(TableOfContentsBlot);

  if (props.readOnly != true) {
    Quill.register(ImageUploadBlot);
  }
});
const content = ref();

const onQuillReady = (ev: any) => {
  content.value = props.modelValue ? new Delta(props.modelValue) : null;

  if (!editor.value) return;

  // @ts-ignore
  const quill = editor.value.getQuill();

  if (!quill) return;

  const toolbar = quill.getModule("toolbar");

  if (!toolbar) return;

  toolbar.addHandler("image", () => {
    imageHandler(quill);
  });

  quill.on("selection-change", (range: any) => {
    if (range) {
      lastCursorPosition.value = range.index;
    }
  });

  quill.root.addEventListener("paste", handlePaste);

  // Listen for text changes to update the TOC
  quill.on("text-change", () => {
    updateTableOfContents(quill);
  });

  // Add click event listener for TOC links
  quill.root.addEventListener("click", (event: any) => {
    const target = event.target as HTMLElement;
    if (
      target &&
      target.tagName === "A" &&
      target.closest(".table-of-contents")
    ) {
      event.preventDefault();

      const editorElement = document.getElementsByClassName("ql-editor")[0];

      // INdex of "A" tag in the table-of-contents .list
      const index = target.getAttribute("data-index");

      if (index === null) return;

      const intIndex = parseInt(index);

      // Get all elements with the class .section-header within editorElement
      const sectionHeaders = editorElement.querySelectorAll(".section-header");

      const sectionHeader = sectionHeaders[intIndex];

      // Convert NodeList to an array and find the element that matches targetContents
      // const sectionHeader = Array.from(sectionHeaders).find((header: any) => {
      //   return header.textContent.includes(targetContents);
      // });

      // Scroll the section header into view
      if (sectionHeader) {
        sectionHeader.scrollIntoView({ block: "start" });
      }
    }
  });
};

const updateTableOfContents = (quill: Quill) => {
  const headers = getSectionHeaders(quill);

  // Generate the TOC HTML

  const tocHTML = headers
    .map((header, index) => {
      return `<div class='flex flex-row items-center py-1'>    
        <a href="javascript:void(0)" data-index="${index}">${index + 1}. ${
        header.text
      }</a>
      </div>`;
    })
    .join("");

  // Update the content of all TOC blots
  const blots = (quill.scroll as any).descendants(TableOfContentsBlot);
  blots.forEach((blot: any) => {
    // Find the ol tag inside of the blot
    const ol = blot.domNode.querySelector(".list");
    if (!ol) return;
    ol.innerHTML = tocHTML;
  });
};

const getSectionHeaders = (quill: Quill) => {
  const headers = [] as any[];
  const lines = quill.getLines();

  lines.forEach((line: any) => {
    const formats = line.formats();

    if (formats && formats["section-header"]) {
      const text = line.domNode.innerText;
      const index = quill.getIndex(line);
      headers.push({ text, index });
    }
  });

  return headers;
};

const handlePaste = async (e: ClipboardEvent) => {
  const quillEditor = editor.value;
  if (!quillEditor) return;

  // Capture the current selection index before preventing default
  const cursorPosition = lastCursorPosition.value;

  // Get the clipboard data
  const clipboardData = e.clipboardData || (window as any).clipboardData;
  let imagePasted = false;

  if (clipboardData && clipboardData.items) {
    const text = clipboardData.getData("text/plain");

    if (text) {
      // If text exists, allow Quill to handle it as usual
      return; // Let QuillJS handle the paste event
    }

    const items = clipboardData.items;
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf("image") !== -1) {
        // Prevent the default paste action only for images
        e.preventDefault();
        imagePasted = true;

        const file = items[i].getAsFile();
        // Upload the image and insert it at the current cursor position
        await uploadAndInsertImage(file, cursorPosition);
      }
    }
  }

  // If no image was pasted, allow the default behavior to handle text or other content
  if (!imagePasted) {
    // Allow default paste behavior for non-image content
  }
};

const uploadAndInsertImage = (file: File, cursorPosition: number) => {
  const quillEditor = editor.value;
  if (!quillEditor) return;

  // @ts-ignore
  const quill = quillEditor.getQuill();
  if (!quill) return;

  // Generate a unique ID for this placeholder
  const id = uuidv4();

  // Insert placeholder image at the captured cursor position
  const placeholderSrc = URL.createObjectURL(file);
  quill.insertEmbed(cursorPosition, "imageUpload", {
    src: placeholderSrc,
    id: id,
  });

  // Start the upload
  const uploadTask = uploadImage(file);

  // Monitor the upload progress
  uploadTask.on(
    "state_changed",
    (snapshot: any) => {
      const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
      updatePlaceholderProgress(id, progress);
    },
    (error: any) => {
      console.error("Error uploading file:", error);
      useBaseToast("Error uploading file", "error");
      removePlaceholderImage(id);
    },
    () => {
      getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
        replacePlaceholderWithImage(id, downloadURL, cursorPosition);
      });
    }
  );
};

const updatePlaceholderProgress = (id: string, progress: number) => {
  const quillEditor = editor.value;
  if (!quillEditor) return;

  // @ts-ignore
  const quill = quillEditor.getQuill();

  const containers = quill.root.querySelectorAll(
    `div.image-uploading-container[data-id="${id}"]`
  );

  containers.forEach((container: any) => {
    const progressBar = container.querySelector(".progress-bar");
    if (progressBar) {
      progressBar.style.width = `${progress}%`;
    }
  });
};

const replacePlaceholderWithImage = (
  id: string,
  url: string,
  cursorPosition: number
) => {
  const quillEditor = editor.value;
  if (!quillEditor) return;

  // @ts-ignore
  const quill = quillEditor.getQuill();

  const containers = quill.root.querySelectorAll(
    `div.image-uploading-container[data-id="${id}"]`
  );

  containers.forEach((container: any) => {
    const blot = Quill.find(container);
    if (blot) {
      const index = quill.getIndex(blot);
      quill.deleteText(index, 1, Quill.sources.SILENT);
      quill.insertEmbed(index, "image", url, Quill.sources.USER);
    }
  });

  quill.setSelection(cursorPosition + 1, 0, Quill.sources.SILENT);
};

const removePlaceholderImage = (id: string) => {
  const quillEditor = editor.value;
  if (!quillEditor) return;

  // @ts-ignore
  const quill = quillEditor.getQuill();

  const containers = quill.root.querySelectorAll(
    `div.image-uploading-container[data-id="${id}"]`
  );

  containers.forEach((container: any) => {
    const blot = Quill.find(container);
    if (blot) {
      blot.deleteAt(0, 1);
    }
  });
};

const uploadImage = (file: File): UploadTask => {
  const imageName = uuidv4(); // Generate a unique name for the image
  const storage = useFirebaseStorage();
  const imageRef = storageRef(storage, `/public-resources/${imageName}`);

  const uploadTask = uploadBytesResumable(imageRef, file);
  return uploadTask;
};

const imageHandler = (editor: Quill) => {
  const input = document.createElement("input");
  input.setAttribute("type", "file");
  input.click();

  input.onchange = async () => {
    const file = input.files?.[0];
    if (!file) return;

    try {
      // Get the current selection index
      const selection = editor.getSelection();
      const cursorPosition = selection ? selection.index : 0;

      await uploadAndInsertImage(file, cursorPosition);
    } catch (error) {
      console.error("Error uploading file:", error);
    }
  };
};

// const insertToEditor = (url: string, editor: Quill) => {
//   const range = editor.getSelection(true);
//   editor.insertEmbed(range.index, "image", url);
// };

const toolbar =
  props.readOnly || props.disabled ? ref([]) : ref(`#${quillToolbarId.value}`);

const onKeyDown = (event: KeyboardEvent) => {
  // if (event.shiftKey && event.key === '"') {
  //   event.preventDefault();
  //   formatQuotes();
  // } else if (event.key == "Backspace" || event.key == "Delete") {
  //   checkQuotes(event);
  // } else if (event.key === "Æ" && event.altKey) {
  //   event.preventDefault();
  //   formatQuotes("”");
  // }

  if (event.key == " ") {
    checkEmoji();
  }
};

const checkEmoji = () => {
  const quillEditor = editor.value;

  if (!quillEditor) {
    return;
  }
  // @ts-ignore
  const quill = quillEditor.getQuill();

  const selection = quill.getSelection();
  if (!selection) return;
  const [line, offset] = quill.getLine(selection.index);

  const textBefore = line.domNode.textContent.substring(offset - 2, offset);
  const matchingEmoji = emojiShortcuts.value[textBefore];

  if (matchingEmoji) {
    // Delete the range of text
    quill.deleteText(selection.index - 2, 2);

    // Insert new text (emoji in this case) at the start index
    quill.insertText(selection.index - 2, matchingEmoji);
  }
};

const onCheckQuotes = () => {
  const quillEditor = editor.value;

  if (!quillEditor) {
    return;
  }

  // @ts-ignore
  const quill = quillEditor.getQuill();
  const text = quill.getText();
  const length = text.length;

  let cursor = 0;
  while (cursor < length) {
    // Find the index of the next quote (straight, open, or close)
    const quoteIndex = text.substring(cursor).search(/["“”]/);
    if (quoteIndex === -1) break; // No more quotes found

    const absoluteIndex = cursor + quoteIndex;

    const replacementQuote = useGetSmartQuoteReplacement(text, absoluteIndex);

    if (replacementQuote) {
      // Create the delta for the replacement
      const delta = new Delta()
        .retain(absoluteIndex) // Retain all text before the quote
        .delete(1) // Delete the current quote
        .insert(replacementQuote); // Insert the correct quote

      // Apply the delta
      quill.updateContents(delta);
    }

    // Move cursor forward
    cursor = absoluteIndex + 1;
  }
};

const emojis = ref(["😀", "😊", "😲", "👍", "👏", "🎉", "🎯", "💯", "←", "→"]);

const emojiShortcuts = ref<{ [key: string]: string }>({
  ":)": "😀",

  ":3": "😊",

  ":>": "😆",
  ":^)": "😆",
  "->": "→",
  "<-": "←",
  "=>": "→",
  "<=": "←",
});
const insertText = (text: string, includeLineBreak: boolean = true) => {
  const quillEditor = editor.value;

  if (!quillEditor) {
    return;
  }

  // @ts-ignore
  const quill = quillEditor.getQuill();
  const range = quill.getSelection(true);

  if (!range) {
    return;
  }

  quill.insertText(range.index, text, Quill.sources.USER);

  if (includeLineBreak) {
    quill.insertText(range.index + text.length, "\n", Quill.sources.USER);
  }

  quill.setSelection(range.index + text.length, Quill.sources.SILENT);
};

const editorContainer = ref();

const formats = [
  "bold",
  "italic",
  "underline",
  "strike",
  "header",
  "size",
  "color",
  "background",
  "link",
  "image",
  "video",
  "section-header",
  "table-of-contents",
  "imageUpload",
  "list", // For ordered and bullet lists
  "indent", // For indentation
  "width",
  "height",
  "align",
];

// Handler for the SectionHeader button
const sectionHeaderHandler = () => {
  // @ts-ignore
  const quill = editor.value.getQuill();
  const range = quill.getSelection();
  if (range) {
    quill.formatLine(range.index, range.length, "section-header", true);
  }
};

// Handler for the TableOfContents button
const tocHandler = () => {
  // @ts-ignore
  const quill = editor.value.getQuill();
  const range = quill.getSelection(true);
  quill.insertEmbed(range.index, "table-of-contents", true, Quill.sources.USER);
};

const onUpdate = (event: any) => {
  emit("update:modelValue", event.ops);

  // Emit quill plaintext
  const quillEditor = editor.value;
  if (!quillEditor) return;

  const text = quillEditor.getText();
  emit("update:plaintext", text);
};

const customModules = computed(() => {
  if (props.readOnly == true || props.disabled == true) return undefined;

  return {
    name: "blotFormatter",
    module: BlotFormatter,
    options: {},
  };
});

const isFocused = ref(false);

const onFocus = () => {
  isFocused.value = true;
};

const onBlur = () => {
  isFocused.value = false;
};
</script>

<template>
  <div
    class="flex flex-col base-text-editor-container"
    :class="{
      disabled: disabled,
      'read-only': readOnly,
    }"
  >
    <div
      v-if="readOnly != true && disabled != true"
      :id="quillToolbarId"
      class="bg-surface border !border-medium-tint rounded mb-2 flex flex-row items-center justify-between w-full"
      :class="{
        '!mb-0': readOnly,
      }"
    >
      <div class="flex flex-row items-center">
        <!-- Heading dropdown -->
        <select
          v-if="showFormattingOptions && showAdvancedToolbar"
          class="ql-header"
        >
          <option selected />
          <option value="1" />
          <option value="2" />
          <option value="3" />
          <option value="4" />
          <option value="5" />
          <option value="6" />
        </select>
        <div v-if="showFormattingOptions && showAdvancedToolbar" class="mx-2">
          |
        </div>
        <button
          v-if="showFormattingOptions && showAdvancedToolbar"
          v-tooltip="'Section'"
          class="section-header-button"
          @click="sectionHeaderHandler"
        >
          <Icon name="mdi:format-header-pound" />
        </button>
        <button
          v-if="showFormattingOptions && showAdvancedToolbar"
          v-tooltip="'Table of Contents'"
          class="table-of-contents-button"
          @click="tocHandler"
        >
          <Icon name="oui:table-of-contents" />
        </button>
        <div v-if="showFormattingOptions && showAdvancedToolbar" class="mx-2">
          |
        </div>
        <button v-if="showFormattingOptions" class="ql-bold" />
        <button v-if="showFormattingOptions" class="ql-italic" />
        <button v-if="showFormattingOptions" class="ql-underline" />
        <button v-if="showFormattingOptions" class="ql-strike" />
        <div v-if="showFormattingOptions && showAdvancedToolbar" class="mx-2">
          |
        </div>
        <button
          v-if="showFormattingOptions && showAdvancedToolbar"
          class="ql-list"
          value="ordered"
        />
        <button
          v-if="showFormattingOptions && showAdvancedToolbar"
          class="ql-list"
          value="bullet"
        />
        <button
          v-if="showFormattingOptions && showAdvancedToolbar"
          class="ql-indent"
          value="-1"
        />
        <!-- Indent less -->
        <button
          v-if="showFormattingOptions && showAdvancedToolbar"
          class="ql-indent"
          value="+1"
        />
        <select
          class="ql-align"
          v-if="showFormattingOptions && showAdvancedToolbar"
        >
          <option selected></option>
          <option value="center"></option>
          <option value="right"></option>
          <option value="justify"></option>
        </select>
        <!-- Indent more -->
        <div v-if="showFormattingOptions && showAdvancedToolbar" class="mx-2">
          |
        </div>
        <button
          v-if="showFormattingOptions && showAdvancedToolbar"
          class="ql-link"
        />
        <button
          v-if="showFormattingOptions && showAdvancedToolbar"
          class="ql-image"
        />
        <button
          v-if="showFormattingOptions && showAdvancedToolbar"
          class="ql-video"
        />

        <div v-if="showFormattingOptions && showAdvancedToolbar" class="mx-2">
          |
        </div>
        <!-- Text color picker -->
        <select
          v-if="showFormattingOptions && showAdvancedToolbar"
          class="ql-color"
        />
        <select
          v-if="showFormattingOptions && showAdvancedToolbar"
          class="ql-background"
        />

        <div v-if="showFormattingOptions && showAdvancedToolbar" class="mx-2">
          |
        </div>

        <BasePopover
          side="top"
          trigger-class="inline-flex items-center justify-center align-middle"
        >
          <span
            class="inline-flex items-center justify-center align-middle h-[12px]"
          >
            😊
          </span>
          <template #content>
            <div class="bg-surface grid grid-cols-6 p-1 justify-start">
              <BaseIconButton
                v-for="(emoji, index) in emojis"
                :key="index"
                @click="insertText(emoji, false)"
              >
                {{ emoji }}
              </BaseIconButton>
            </div>
          </template>
        </BasePopover>
      </div>
    </div>
    <div
      ref="editorContainer"
      class="editor-container border border-medium-tint rounded grow overflow-auto"
      :class="{
        'outline outline-focus outline-2 -outline-offset-1': isFocused,
      }"
    >
      <QuillEditor
        ref="editor"
        :toolbar="toolbar"
        theme="snow"
        :options="{
          bounds: editorContainer,
          formats: formats,
        }"
        :modules="customModules"
        :content="content"
        :read-only="readOnly || disabled"
        :enable="!readOnly && !disabled"
        :disabled="disabled"
        @update:content="onUpdate"
        @ready="onQuillReady"
        @keydown="onKeyDown"
        @keyup="onCheckQuotes"
        @focus="onFocus"
        @blur="onBlur"
      />
    </div>
  </div>
</template>

<style>
.base-text-editor-container .ql-toolbar.ql-snow + .ql-container.ql-snow {
  border-top: unset;
}

.base-text-editor-container .ql-container.ql-snow {
  border: unset;
}

.base-text-editor-container .ql-container,
.base-text-editor-container .ql-editor {
  font-family: lato !important;

  @apply bg-surface;
}

.base-text-editor-container .disabled .ql-toolbar,
.base-text-editor-container .read-only .ql-toolbar {
  display: none;
}

.base-text-editor-container .disabled #quill-toolbar,
.base-text-editor-container .read-only #quill-toolbar {
  margin-bottom: 0px;
}

.base-text-editor-container .read-only .ql-container,
.base-text-editor-container .read-only .ql-editor,
.base-text-editor-container .read-only .editor-container {
  @apply bg-transparent;

  @apply text-body-1;

  border: none !important;
}

.base-text-editor-container .disabled .ql-container,
.base-text-editor-container .disabled .ql-editor {
  @apply !text-[#030202];
}

.base-text-editor-container .ql-editor {
  border: unset;
  @apply p-2 !important;
}

.base-text-editor-container .section-header {
  border-bottom: 1px solid black;
  margin-bottom: 8px !important;
  scroll-margin-top: 16px;
}

.base-text-editor-container .table-of-contents a {
  cursor: pointer;
  @apply text-primary-default;
  @apply !no-underline;
}

.base-text-editor-container .image-uploading-container {
  position: relative;
  display: inline-block;
}

.base-text-editor-container .image-uploading-container .progress-bar {
  height: 8px;
  @apply bg-success-default;
  width: 0%;
  @apply mb-2;
  @apply rounded;
}

.base-text-editor-container .image-uploading-container img {
  max-width: 100%;
}

.base-text-editor-container.read-only .ql-toolbar,
.base-text-editor-container.disabled .ql-toolbar {
  display: none;
}

.base-text-editor-container.read-only .ql-container,
.base-text-editor-container.disabled .ql-container,
.base-text-editor-container.read-only .ql-editor,
.base-text-editor-container.disabled .ql-editor,
.base-text-editor-container.read-only .editor-container,
.base-text-editor-container.disabled .editor-container {
  background-color: transparent !important;
  border: none !important;
  padding: 0 !important;
}

.base-text-editor-container .ql-editor ul, .base-text-editor-container .ql-editor ol {
  @apply pl-1;
  @apply mt-2;
}
.base-text-editor-container .ql-editor li {
  @apply mb-2;
}

</style>
